import { useState, useEffect, useMemo } from 'react';
import { Search, User } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useFriends } from '@/hooks/useFriends';

interface Friend {
  user_id: string;
  full_name: string | null;
  avatar_url: string | null;
  preferred_sports?: string[];
  skill_level?: string;
}

interface FriendSelectorProps {
  onSelectFriend: (friend: Friend) => void;
  selectedFriend: Friend | null;
  eventSport: string;
}

export const FriendSelector = ({ onSelectFriend, selectedFriend, eventSport }: FriendSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { friends, loading, error } = useFriends();

  // Filter friends based on search query and mutual following
  const filteredFriends = useMemo(() => {
    if (!friends) return [];
    
    // Get mutual friends (where both follow each other)
    const mutualFriends = friends.filter(friend => friend.status === 'accepted' && friend.friend_profile?.user_id);
    
    // Filter by search query if provided
    if (searchQuery.trim().length === 0) {
      return mutualFriends;
    }
    
    const query = searchQuery.toLowerCase();
    return mutualFriends.filter(friend => {
      const profile = friend.friend_profile;
      if (!profile) return false;
      
      const name = profile.full_name?.toLowerCase() || '';
      
      return name.includes(query);
    });
  }, [friends, searchQuery]);

  const handleFriendSelect = (friend: any) => {
    const profile = friend.friend_profile;
    if (!profile) return;
    
    const friendData: Friend = {
      user_id: profile.user_id,
      full_name: profile.full_name,
      avatar_url: profile.avatar_url,
      preferred_sports: profile.preferred_sports,
      skill_level: profile.skill_level
    };
    onSelectFriend(friendData);
  };

  const getInitials = (name: string | null) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Buscar en mis amigos..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {selectedFriend && (
        <Card className="border border-primary bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Avatar className="w-12 h-12">
                <AvatarImage src={selectedFriend.avatar_url || ''} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {getInitials(selectedFriend.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h4 className="font-medium">{selectedFriend.full_name || 'Usuario'}</h4>
                <p className="text-sm text-muted-foreground">ID: {selectedFriend.user_id.slice(0, 8)}</p>
                {selectedFriend.skill_level && (
                  <Badge variant="outline" className="mt-1">
                    {selectedFriend.skill_level}
                  </Badge>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSelectFriend(null as any)}
              >
                Cambiar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!selectedFriend && (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {loading ? (
            <div className="text-center py-4 text-muted-foreground">
              Cargando amigos...
            </div>
          ) : error ? (
            <div className="text-center py-4 text-muted-foreground">
              Error al cargar amigos
            </div>
          ) : filteredFriends.length > 0 ? (
            filteredFriends.map((friend) => {
              const profile = friend.friend_profile;
              if (!profile) return null;
              
              return (
                <Card key={friend.id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => handleFriendSelect(friend)}>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={profile.avatar_url || ''} />
                        <AvatarFallback>
                          {getInitials(profile.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">{profile.full_name || 'Usuario'}</h4>
                        <p className="text-xs text-muted-foreground">ID: {profile.user_id.slice(0, 8)}</p>
                        <div className="flex gap-1 mt-1">
                          {profile.skill_level && (
                            <Badge variant="outline" className="text-xs">
                              {profile.skill_level}
                            </Badge>
                          )}
                          {profile.preferred_sports?.includes(eventSport) && (
                            <Badge variant="secondary" className="text-xs">
                              {eventSport}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFriendSelect(friend);
                        }}
                      >
                        Seleccionar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          ) : friends && friends.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <User className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Aún no tienes amigos</p>
              <p className="text-xs mt-1">Agrega amigos desde tu perfil para poder seleccionarlos aquí</p>
            </div>
          ) : (
            <div className="text-center py-4 text-muted-foreground">
              No se encontraron amigos con ese nombre
            </div>
          )}
        </div>
      )}
    </div>
  );
};